import { ProposeChange, type ProposeField } from '@/components/propose-change';

interface RefundOrCorrectProps {
  /** The wallets it may book to: one is fixed, two are picked from. */
  wallets: readonly { id: string; number: string; label: string }[];
  currency: string;
  /** The transaction it fixes, fixed by the page; else the maker may name one. */
  transactionId?: string;
}

/**
 * Proposes an `adjustment`: a refund or correction booked by Sao alone,
 * optionally for the transaction it fixes. Finance picks own funds or
 * write-offs on the approval that applies it. Hidden from staff who cannot
 * propose one.
 */
export function RefundOrCorrect({
  wallets,
  currency,
  transactionId,
}: Readonly<RefundOrCorrectProps>) {
  const [only] = wallets;
  if (!only) return null;
  const fields: ProposeField[] = [
    ...(wallets.length > 1
      ? [
          {
            name: 'wallet_id',
            label: 'Wallet',
            kind: 'select' as const,
            options: wallets.map((wallet) => ({
              value: wallet.id,
              label: `${wallet.label} (${wallet.number})`,
            })),
          },
        ]
      : []),
    {
      name: 'direction',
      label: 'Direction',
      kind: 'select',
      options: [
        { value: 'credit', label: 'Credit the wallet' },
        { value: 'debit', label: 'Debit the wallet' },
      ],
    },
    {
      name: 'amount_minor',
      label: `Amount (${currency})`,
      kind: 'amount',
      currency,
    },
    ...(transactionId
      ? []
      : [
          {
            name: 'transaction_id',
            label: 'For transaction',
            kind: 'text' as const,
            optional: true,
            hint: 'Optional. The id of the transaction this fixes, from its page. It must have moved money in or out of this wallet.',
          },
        ]),
  ];
  return (
    <ProposeChange
      kind="adjustment"
      payload={{
        ...(wallets.length > 1 ? {} : { wallet_id: only.id }),
        ...(transactionId ? { transaction_id: transactionId } : {}),
      }}
      label="Refund or correct"
      title={
        transactionId
          ? 'Refund or correct for this transaction'
          : 'Refund or correct this wallet'
      }
      description="Booked by Sao alone. Finance picks whether it comes from own funds or write-offs when approving."
      fields={fields}
      evidence={
        transactionId ? [{ type: 'transaction', ref: transactionId }] : []
      }
    />
  );
}
